import React, { useState, useEffect, useRef, useContext } from 'react';
import { Modal, Form, Button, Row, Col, Alert, Card, Badge } from 'react-bootstrap';
import Icon from '../../../../components/icon/Icon';
import CarSelector from './CarSelector';
import SearchableDropdown from '../../../../components/common/SearchableDropdown';
import { getAPI } from '../../../../apis/backend/apiNode';
import { loadCars } from 'pages/planning/hooks/planningService';
import CompanieContext from 'contexts/companieContext';
import moment from 'moment';
import { formatExactTime } from 'pages/comptabilite/utils/functions';
import FicheSearchDropdown from 'components/FicheSearchDropdown/FicheSearchDropdown';

const EditFormModal = ({
  show,
  onHide,
  intervention,
  onSave,
  loading = false,
  INTERVENTION_TYPES = [],
  INTERVENTION_MOTIFS = [],
  reloadData
}) => {
  const [formData, setFormData] = useState({
    date_debut: '',
    date_fin: '',
    type: '',
    motif: '',
    statut: 'planifie',
    selectedCars: [],
    client: '',
    fournisseur: '',
    fiche: '',
    companyId: ""
  });
  const { companies } = useContext(CompanieContext);

  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedFournisseur, setSelectedFournisseur] = useState(null);
  const [selectedFiche, setSelectedFiche] = useState(null);
  const [errors, setErrors] = useState({});
  const [cars, setCars] = useState([]);
  const [loadingCars, setLoadingCars] = useState(false);
  const companiesRef = useRef([]);

  // Fetch cars
  useEffect(() => {
    const fetchCars = async () => {
      if (!show) return;
      setLoadingCars(true);
      try {
        const carFilters = {};
        const carData = await loadCars(carFilters, companiesRef.current);
        setCars(carData || []);
      } catch (error) {
        console.error('Error loading cars:', error);
        setCars([]);
      } finally {
        setLoadingCars(false);
      }
    };
    fetchCars();
  }, [show]);

  // Fetch clients function
  const fetchClients = async (searchTerm = '', signal = null) => {
    try {
      const queryParams = new URLSearchParams({
        limit: '20',
        page: '1',
      });
      if (searchTerm) {
        queryParams.append('search', searchTerm);
      }
      const result = await getAPI('store2000_client', `?${queryParams.toString()}`, signal);
      if (result?.success) {
        return result.clients || [];
      }
      return [];
    } catch (error) {
      console.error('Erreur recherche clients:', error);
      return [];
    }
  };

  // Fetch fournisseurs function
  const fetchFournisseurs = async (searchTerm = '', signal = null) => {
    try {
      const queryParams = new URLSearchParams({
        limit: '20',
        page: '1',
      });
      if (searchTerm) {
        queryParams.append('search', searchTerm);
      }
      const result = await getAPI('achat', `/fournisseurs?${queryParams.toString()}`, signal);
      if (result?.success) {
        return result.data?.fournisseurs || result.fournisseurs || [];
      }
      return [];
    } catch (error) {
      console.error('Erreur recherche fournisseurs:', error);
      return [];
    }
  };

  // Formatter la date pour l'input datetime-local
  const formatDateTimeForInput = (date) => {
    if (!date) return '';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      const pad = (n) => n.toString().padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Initialiser le formulaire
  useEffect(() => {
    if (intervention && show) {
      const initialCars = intervention.cars?.map((c) =>
        typeof c === 'object' ? c._id : c
      ) || [];

      setFormData({
        date_debut: moment.utc(intervention.date_debut).format("YYYY-MM-DDTHH:mm"),
        date_fin: moment.utc(intervention.date_fin).format("YYYY-MM-DDTHH:mm"),
        type: intervention.type || '',
        motif: intervention.motif || '',
        statut: intervention.statut || 'planifie',
        selectedCars: initialCars,
        client: intervention.client?._id || intervention.client || '',
        fournisseur: intervention.fournisseur?._id || intervention.fournisseur || '',
        fiche: intervention.fiche?._id || '',
        companyId: intervention?.companyId?._id || intervention?.cars?.[0]?.companyId || ""
      });

      // Set selected client/fournisseur/fiche
      if (intervention.type === 'enlevement') {
        setSelectedFournisseur(intervention.fournisseur || null);
        setSelectedClient(null);
      } else {
        setSelectedClient(intervention.client || null);
        setSelectedFournisseur(null);
      }
      setSelectedFiche(intervention.fiche || null);
    }
  }, [intervention, show]);

  // Handle type change
  useEffect(() => {
    if (formData.type === 'enlevement') {
      setFormData(prev => ({
        ...prev,
        client: ''
      }));
      setSelectedClient(null);
    } else if (formData.type !== 'enlevement' && formData.type !== '') {
      setFormData(prev => ({
        ...prev,
        fournisseur: ''
      }));
      setSelectedFournisseur(null);
    }
  }, [formData.type]);

  // Gestion des changements
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Handle client selection
  const handleClientSelect = (client) => {
    if (client && client._id) {
      setSelectedClient(client);
      setFormData(prev => ({
        ...prev,
        client: client._id,
      }));
    }
  };

  // Handle fournisseur selection
  const handleFournisseurSelect = (fournisseur) => {
    if (fournisseur && fournisseur._id) {
      setSelectedFournisseur(fournisseur);
      setFormData(prev => ({
        ...prev,
        fournisseur: fournisseur._id,
      }));
    }
  };

  // Handle fiche selection
  const handleFicheSelect = (fiche) => {
    if (fiche && fiche._id) {
      setSelectedFiche(fiche);
      setFormData(prev => ({
        ...prev,
        fiche: fiche._id,
      }));
    } else if (fiche === null) {
      setSelectedFiche(null);
      setFormData(prev => ({
        ...prev,
        fiche: '',
      }));
    }
  };

  // Gestion des voitures
  const handleToggleCar = (carId) => {
    setFormData((prev) => ({
      ...prev,
      selectedCars: prev.selectedCars.includes(carId)
        ? prev.selectedCars.filter((id) => id !== carId)
        : [...prev.selectedCars, carId]
    }));
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.date_debut)
      newErrors.date_debut = 'La date de début est requise';
    if (!formData.date_fin)
      newErrors.date_fin = 'La date de fin est requise';
    if (formData.date_debut && formData.date_fin) {
      const start = new Date(formData.date_debut);
      const end = new Date(formData.date_fin);
      if (isNaN(start.getTime())) newErrors.date_debut = 'Date de début invalide';
      if (isNaN(end.getTime())) newErrors.date_fin = 'Date de fin invalide';
      if (end <= start) {
        newErrors.date_fin = 'La date de fin doit être après la date de début';
      }
    }
    if (!formData.type)
      newErrors.type = "Le type d'intervention est requis";
    if (!formData.motif)
      newErrors.motif = 'Le motif est requis';
    if (!formData.statut)
      newErrors.statut = 'Le statut est requis';
    if (formData.selectedCars.length === 0)
      newErrors.cars = 'Au moins une voiture est requise';

    // Validate entity based on type
    if (formData.type === 'enlevement') {
      if (!formData.fournisseur) newErrors.fournisseur = 'Fournisseur requis pour le type "enlevement"';
    } else if (formData.type !== '') {
      if (!formData.client) newErrors.client = 'Client requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const updateData = {
      date_debut: formatExactTime(formData.date_debut),
      date_fin: formatExactTime(formData.date_fin),
      type: formData.type,
      motif: formData.motif,
      statut: formData.statut == "a_planifie" ? "planifie" : formData.statut,
      cars: formData.selectedCars,
      companyId: formData?.companyId,
      fiche: selectedFiche?._id || null,

    };

    // Add client or fournisseur based on type
    if (formData.type === 'enlevement') {
      updateData.fournisseur = formData.fournisseur;
    } else {
      updateData.client = formData.client;
    }

    onSave(updateData);
  };

  // Render client option
  const renderClientOption = (client) => {
    const mainEmail = client.emails && client.emails.length > 0 ? client.emails[0] : '';
    const mainPhone = client.telephones && client.telephones.length > 0 ? client.telephones[0] : '';
    return (
      <div className='dropdown-option'>
        <div className='d-flex align-items-center justify-content-between'>
          <div className='flex-grow-1'>
            <div className='fw-medium text-dark'>{client.nom}</div>
            {mainEmail && (
              <small className='text-muted d-block mt-1'>
                <i className='fas fa-envelope me-1'></i>
                {mainEmail}
              </small>
            )}
            {mainPhone && (
              <small className='text-muted d-block mt-1'>
                <i className='fas fa-phone me-1'></i>
                {mainPhone}
              </small>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render fournisseur option
  const renderFournisseurOption = (fournisseur) => {
    return (
      <div className='dropdown-option'>
        <div className='d-flex align-items-center justify-content-between'>
          <div className='flex-grow-1'>
            <div className='fw-medium text-dark'>{fournisseur.nom}</div>
            {fournisseur.email && (
              <small className='text-muted d-block mt-1'>
                <i className='fas fa-envelope me-1'></i>
                {fournisseur.email}
              </small>
            )}
            {fournisseur.telephone && (
              <small className='text-muted d-block mt-1'>
                <i className='fas fa-phone me-1'></i>
                {fournisseur.telephone}
              </small>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      backdrop="static"
      className="edit-form-modal"
    >
      <style jsx global>{`
        .edit-form-modal .modal-content {
          background-color: #ffffff;
          border: none;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
        }
        .edit-form-modal .modal-header {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-radius: 12px 12px 0 0;
        }
        .edit-form-modal .modal-body {
          max-height: 70vh;
          overflow-y: auto;
        }
      `}</style>

      <Modal.Header closeButton className="border-0">
        <Modal.Title className="d-flex align-items-center text-dark">
          <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
            <Icon icon="Edit" className="text-primary" />
          </div>
          <div>
            <h5 className="fw-bold mb-0">Modifier l'intervention</h5>
            <small className="text-muted">
              {intervention?.reference || 'Mettre à jour les informations'}
            </small>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {Object.keys(errors).length > 0 && (
            <Alert variant="danger" className="border-0 shadow-sm">
              <Icon icon="Error" className="me-2" />
              Veuillez corriger les erreurs ci-dessous
            </Alert>
          )}

          <Row className="g-4">
            {/* Société */}
            <Col md={12}>
              <Form.Group>
                <Form.Label className='fw-semibold'>Société *</Form.Label>
                <Form.Select
                  id='companyId'
                  name='companyId'
                  value={formData.companyId}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    setFormData({ ...formData, companyId: selectedId });
                  }}
                >
                  <option value=''>Sélectionner une société</option>
                  {companies.map((societe) => (
                    <option key={societe._id} value={societe._id}>
                      {societe.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Entity Selection (Client/Fournisseur) */}
            <Col md={12}>
              <Card className='border shadow-sm mb-3'>
                <Card.Header className='bg-light'>
                  <strong>
                    {formData.type === 'enlevement' ? '👥 Fournisseur' : '👤 Client'}
                  </strong>
                </Card.Header>
                <Card.Body>
                  {formData.type === 'enlevement' ? (
                    <Form.Group>
                      <Form.Label>Fournisseur *</Form.Label>
                      <SearchableDropdown
                        value={selectedFournisseur}
                        onChange={handleFournisseurSelect}
                        fetchOptions={fetchFournisseurs}
                        placeholder='Rechercher un fournisseur...'
                        required={true}
                        displayField='nom'
                        subField='email'
                        renderOption={renderFournisseurOption}
                        clearable={true}
                        className="mb-2"
                        inputClassName="form-control"
                      />
                      {errors.fournisseur && (
                        <div className='text-danger small mt-1'>
                          {errors.fournisseur}
                        </div>
                      )}
                    </Form.Group>
                  ) : (
                    <Form.Group>
                      <Form.Label>Client *</Form.Label>
                      <SearchableDropdown
                        value={selectedClient}
                        onChange={handleClientSelect}
                        fetchOptions={fetchClients}
                        placeholder='Rechercher un client...'
                        required={true}
                        displayField='nom'
                        subField='emails.0'
                        renderOption={renderClientOption}
                        clearable={true}
                        className="mb-2"
                        inputClassName="form-control"
                      />
                      {errors.client && (
                        <div className='text-danger small mt-1'>
                          {errors.client}
                        </div>
                      )}
                    </Form.Group>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* NEW: Fiche Card */}
            <Col md={12}>
              <Card className='border shadow-sm mb-3'>
                <Card.Header className='bg-light'>
                  <strong>📄 Fiche Google</strong>
                  {selectedFiche && (
                    <Badge bg="success" className="ms-2">
                      Sélectionnée
                    </Badge>
                  )}
                </Card.Header>
                <Card.Body>
                  <FicheSearchDropdown
                    value={selectedFiche}
                    onChange={handleFicheSelect}
                    placeholder="Rechercher une fiche par nom ou adresse..."
                    label="Fiche"
                    id="fiche-search-edit"
                  />
                  {selectedFiche && selectedFiche.place_id && (
                    <div className="mt-3 p-2 bg-info bg-opacity-10 rounded border border-info">
                      <small className="text-info d-block">
                        <i className="fas fa-link me-1"></i>
                        <strong>Lien avis Google:</strong>
                      </small>
                      <a
                        href={`https://search.google.com/local/writereview?placeid=${selectedFiche.place_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="small text-info text-break"
                      >
                        https://search.google.com/local/writereview?placeid={selectedFiche.place_id}
                      </a>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>

            {/* Dates */}
            <Col md={6}>
              <Form.Group>
                <Form.Label>Date de début *</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={moment.utc(formData.date_debut).format("YYYY-MM-DDTHH:mm")}
                  onChange={(e) => handleChange('date_debut', e.target.value)}
                  isInvalid={!!errors.date_debut}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.date_debut}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label>Date de fin *</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={moment.utc(formData.date_fin).format("YYYY-MM-DDTHH:mm")}
                  onChange={(e) => handleChange('date_fin', e.target.value)}
                  isInvalid={!!errors.date_fin}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.date_fin}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Type */}
            <Col md={6}>
              <Form.Group>
                <Form.Label>Type d'intervention *</Form.Label>
                <Form.Select
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  isInvalid={!!errors.type}
                >
                  <option value="">Sélectionnez un type</option>
                  {INTERVENTION_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.type}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Motif */}
            <Col md={6}>
              <Form.Group>
                <Form.Label>Motif *</Form.Label>
                <Form.Select
                  value={formData.motif}
                  onChange={(e) => handleChange('motif', e.target.value)}
                  isInvalid={!!errors.motif}
                >
                  <option value="">Sélectionnez un motif</option>
                  {INTERVENTION_MOTIFS.map((motif) => (
                    <option key={motif.value} value={motif.value}>
                      {motif.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.motif}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Voitures */}
            <Col md={12}>
              <Form.Group>
                <Form.Label>Voitures assignées *</Form.Label>
                {loadingCars ? (
                  <div className="text-center p-3">
                    <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                      <span className="visually-hidden">Chargement...</span>
                    </div>
                    Chargement des voitures...
                  </div>
                ) : (
                  <>
                    {errors.cars && (
                      <div className="text-danger small mb-2">
                        {errors.cars}
                      </div>
                    )}
                    <CarSelector
                      cars={cars}
                      selectedCars={formData.selectedCars}
                      onToggleCar={handleToggleCar}
                      isEditing
                    />
                    {cars.length === 0 && (
                      <div className="text-muted text-center p-3">
                        Aucune voiture disponible
                      </div>
                    )}
                  </>
                )}
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={loading || loadingCars}
          >
            Annuler
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading || loadingCars}
          >
            {loading ? 'Sauvegarde...' : 'Enregistrer'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditFormModal;